/* AppraisalComponents/hooks/useUnderlayCascade.js
 * Phase C · C4c Underlay extraction (2026-02c).
 *
 * Owns both Underlay-section useEffects:
 *   1. Retro-seed empty Underlay rows once rates.Underlay loads.
 *      Prefers engine `rate_picks.Underlay` tokens (UND-Default-Covertek);
 *      falls back to inline `covertek 215` regex + any-rate-with-coverage.
 *      Extracted from AppraisalComponentsTab.js:501-544.
 *   2. Cascade `roof_m2 → Underlay roll qty`. Recomputes qty
 *      = ceil(roof_m2 / roll_coverage_m2) whenever roof_m2 changes,
 *      but ONLY for rows that were auto-filled AND aren't in
 *      replacement mode (Iter257). Extracted from AppraisalComponentsTab.js:553-578.
 *
 * State stays in the parent · this hook takes state slices + setGroups.
 */
import { useEffect } from 'react';

export function useUnderlayCascade({
  rates,
  roofM2,
  groupsUnderlay,      // groups.Underlay
  engineRatePicks,
  setGroups,
}) {
  // (1) Retro-seed Covertek 215 + roll-count math.
  useEffect(() => {
    const underlayRates = rates.Underlay || [];
    if (!underlayRates.length) return;
    const m2 = Number(roofM2) || 0;
    // C3c-γ · prefer engine rate_picks · fall back to `covertek 215` regex
    // + any-rate-with-coverage. Same safety-net pattern as C3c-α Fastening.
    const engineTokens = engineRatePicks?.Underlay?.tokens;
    const findByTokens = (tokens) => underlayRates.find(r => {
      const tn = String(r.type_name || '').toLowerCase();
      return tokens.every(t => tn.includes(String(t).toLowerCase()));
    });
    const defaultRate = (engineTokens && engineTokens.length
                          ? findByTokens(engineTokens)
                          : null)
      ?? underlayRates.find(r =>
        String(r.type_name || '').toLowerCase().startsWith('covertek 215')
      )
      ?? underlayRates.find(r => Number(r.roll_coverage_m2) > 0);
    if (!defaultRate) return;
    const underlayRows = groupsUnderlay || [];
    let touched = false;
    const next = underlayRows.map(r => {
      if (r.type_name) return r;
      const coverage = Number(defaultRate.roll_coverage_m2) || 0;
      const computedQty = (coverage > 0 && m2 > 0) ? Math.ceil(m2 / coverage) : 0;
      touched = true;
      return {
        ...r,
        type_name:       defaultRate.type_name,
        buy_cost:        Number(defaultRate.buy_cost)       || 0,
        materials_rate:  Number(defaultRate.materials_rate) || 0,
        labour_rate:     Number(defaultRate.labour_rate)    || 0,
        contractor_rate: Number(defaultRate.contractor_rate)|| 0,
        supplier:        defaultRate.supplier || '',
        supplier_code:   defaultRate.supplier_code || '',
        materials_desc:  defaultRate.materials_desc || '',
        labour_details:  defaultRate.labour_details || '',
        quantity:        computedQty > 0 ? computedQty : Number(r.quantity) || 0,
        qty_auto_filled: computedQty > 0 ? true : !!r.qty_auto_filled,
        qty_auto_source_m2: computedQty > 0 ? m2 : r.qty_auto_source_m2,
      };
    });
    if (touched) setGroups(prev => ({ ...prev, Underlay: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates.Underlay, roofM2, groupsUnderlay, engineRatePicks]);

  // (2) roof_m2 → Underlay roll qty cascade. Auto-filled rows only.
  //     Replacement-mode rows derive qty from picked Sheet rows (Iter257).
  useEffect(() => {
    const m2 = Number(roofM2) || 0;
    if (m2 <= 0) return;
    const underlayRows = groupsUnderlay || [];
    if (!underlayRows.length) return;
    const underlayRates = rates.Underlay || [];
    if (!underlayRates.length) return;
    let touched = false;
    const next = underlayRows.map(r => {
      if (!r.qty_auto_filled) return r;
      if ((r.underlay_mode || 'full') === 'replacement') return r;
      const rateRow = underlayRates.find(x => x.type_name === r.type_name);
      const coverage = Number(rateRow?.roll_coverage_m2) || 0;
      if (coverage <= 0) return r;
      const newQty = Math.ceil(m2 / coverage);
      if (Number(r.quantity) === newQty && Number(r.qty_auto_source_m2) === m2) return r;
      touched = true;
      return { ...r, quantity: newQty, qty_auto_source_m2: m2 };
    });
    if (touched) {
      setGroups(prev => ({ ...prev, Underlay: next }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roofM2, groupsUnderlay, rates.Underlay]);
}
